import { Key, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { formatDateString } from 'enrollment/utils/FieldUtils/fieldUtils';
import {
  convertApiDateToDateTime,
  getInitialValues,
  mapBeneficiaryModel,
} from 'theSource/utils';
import { addNewBeneficiary } from '../../services/slice';
import { PsButtonTuner, PsCheckboxTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import {
  generateRandomSixDigitNumber,
  PSDialog,
} from 'enrollment/utils/common/Utils';
import { FormElement } from 'enrollment/components/Shared/FormElement';

import '../BeneficiarySelectCard/BeneficiarySelectCard.css';

export interface CreateNewBeneficiaryDrawerProps {
  orgBenefitId: number;
  metadata?: any;
  isPrimary: boolean;
  setAddBeneficiaryDrawer: (e: any) => void;
  handleAddToAllBenefits: (e: any) => void;
  coverageLineId?: number;
}

const destructureAddedNewBeneficiaryFunction = (newUpdatedValues: any) => {
  return newUpdatedValues[0];
};

export const CreateNewBeneficiaryDrawer = ({
  orgBenefitId,
  metadata,
  isPrimary,
  setAddBeneficiaryDrawer,
  handleAddToAllBenefits,
  coverageLineId,
}: CreateNewBeneficiaryDrawerProps) => {
  const { t } = useTranslation('pages.enrollmentCreateNewBeneficiaries');
  const dispatch = useDispatch();

  const categories = useMemo(() => mapBeneficiaryModel(metadata), [metadata]);

  const initialValues = useMemo(
    () => getInitialValues(categories),
    [categories]
  );

  const [updatedValues, setUpdatedValues] = useState(initialValues);
  const updatedValuesRef = useRef(updatedValues);

  const [openNewBeneficiaryDrawer, setOpenNewBeneficiaryDrawer] =
    useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState(true);

  useEffect(() => {
    updatedValuesRef.current = updatedValues;
  }, [updatedValues]);

  // Function to handle field validity changes
  const handleFieldValidityChange = (e: any) => {
    const value = e.target.value;

    const drawer = document.getElementById(
      `create-new-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`
    )!;

    const requiredFields = drawer.querySelectorAll('input[required]');

    let allValid = true;

    for (let i = 0; i < requiredFields.length; i++) {
      const inputField = requiredFields[i] as HTMLInputElement;

      if (inputField.type === 'date') {
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: convertApiDateToDateTime(formatDateString(value)),
        }));
      }

      if (inputField.type === 'text') {
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: value.replace('%', ''),
        }));
      }

      if (!inputField.validity.valid) {
        allValid = false;
        break;
      }
    }

    setSaveDisabled(!allValid);
  };

  const handleSubmitCreateDrawer = () => {
    const drawer = document.getElementById(
      `create-new-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`
    )!;

    const values = updatedValuesRef.current;

    const valuesWithEmptyStringsReplaced = Object.entries(values).reduce(
      (newValues, [key, value]) => {
        newValues[key] =
          value === '' || value === null ? null : value!.toString();
        return newValues;
      },
      {} as { [key: string]: string | null }
    );

    const beneficiaryAdded = destructureAddedNewBeneficiaryFunction([
      {
        ...valuesWithEmptyStringsReplaced,
        primary_beneficiary: isPrimary,
        uniq_id: generateRandomSixDigitNumber(),
        coverage_line_id: coverageLineId,
      },
    ]);

    const addToAllBenefitsCheckbox = drawer.querySelector(
      '#add-to-all-benefits-new-beneficiary'
    ) as HTMLInputElement;

    if (addToAllBenefitsCheckbox?.checked) {
      handleAddToAllBenefits(beneficiaryAdded);
    } else {
      dispatch(addNewBeneficiary({ orgBenefitId, beneficiaryAdded }));
    }

    const form = drawer.querySelector(
      `#create-new-beneficiary-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`
    ) as HTMLFormElement;

    form.reset();

    // When the Create New Beneficiary Drawer is closed
    setOpenNewBeneficiaryDrawer(false);

    // This waits for the Create New Beneficiary Drawer to close before closing Add Beneficiary Drawer
    PSDialog.dialogClose(
      `add-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`,
      () => setAddBeneficiaryDrawer(false)
    );
  };

  const handleNewBeneficiaryDrawer = () => {
    setOpenNewBeneficiaryDrawer(true);
  };

  return (
    <>
      <PsButtonTuner
        buttonId="create-new-beneficiaty-btn"
        label="Or Create New One"
        level="tertiary"
        iconRight="caret-right"
        onClick={handleNewBeneficiaryDrawer}
      />

      <OrbitDrawer
        drawerId={`create-new-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
        heading={t('pages.enrollmentCreateNewBeneficiaries:title', {
          beneficiaryType: isPrimary ? 'Primary' : 'Secondary',
        })}
        isOpen={openNewBeneficiaryDrawer}
        footer={true}
        saveButton={t('common:save')}
        saveButtonDisabled={saveDisabled}
        handleClose={() => setOpenNewBeneficiaryDrawer(false)}
        handleSubmit={handleSubmitCreateDrawer}
      >
        <Box
          component="form"
          id={`create-new-beneficiary-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
        >
          <p>Please provide the required information for this beneficiary to continue.</p>
          <div>
            <div>
              {categories.map((category: any, index) => (
                <div key={index}>
                  {category.fields.map(
                    (field: any, index: Key | null | undefined) => (
                      <div key={index}>
                        {field.fieldName === 'allocation' ? (
                          <FormElement
                            inputId={`${field.fieldName}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
                            formField={field.dataType}
                            label={field.label}
                            name={field.fieldName}
                            value={field.value}
                            required={field.required ? true : false}
                            disabled={!field.editable ? true : false}
                            validationText="Must be a number between 0 and 100 and can have up to 2 decimal places"
                            icon="percent"
                            onInput={(e: any) =>
                              field.required && handleFieldValidityChange(e)
                            }
                          />
                        ) : field.fieldName === 'primary_beneficiary' ? (
                          <ps-checkbox
                            data-testid={field.fieldName}
                            input-id={`${field.fieldName}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
                            label={field.label}
                            name={field.fieldName}
                            checked={isPrimary ? true : null}
                            natural
                            extra-class="d-none"
                          ></ps-checkbox>
                        ) : (
                          <FormElement
                            inputId={`${field.fieldName}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
                            formField="text"
                            label={field.label}
                            name={field.fieldName}
                            value={field.value}
                            required={field.required ? true : false}
                            disabled={!field.editable ? true : false}
                            onInput={(e: any) =>
                              field.required && handleFieldValidityChange(e)
                            }
                          />
                        )}
                      </div>
                    )
                  )}
                  <div className="add-to-all-benefits-row">
                    <span className="add-to-all-benefits-label">
                      {t('pages.enrollmentAddBeneficiaries:addToAllBenefits')}
                    </span>
                    <PsCheckboxTuner
                      data-testid="add-to-all-benefits-new-beneficiary"
                      inputId="add-to-all-benefits-new-beneficiary"
                      name="add-to-all-benefits"
                      label={t('pages.enrollmentAddBeneficiaries:addToAllBenefits')}
                      hideLabel
                      className="mobile-drawer-checkbox"
                      extraClass="mt-n2 mb-2"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Box>
      </OrbitDrawer>
    </>
  );
};
